import { Stack, Typography, TextField, Button, Alert, InputAdornment, CircularProgress, Box, Collapse, useTheme } from '@mui/material';
import { Dns, AccountCircle, Lock, Check, CloudDone, LinkOff } from '@mui/icons-material';
import { observer } from 'mobx-lite';
import { MainStoreContextItem } from '../../Stores/MainStore';
import Color from 'color';

const inputSx = (accentColor: string) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        transition: 'box-shadow 0.2s ease',
        '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${Color(accentColor).alpha(0.15).hexa()}`,
        },
    },
});

const IdentityServerConfig: React.FC = observer(() => {
    const mainStore = MainStoreContextItem.useStore();
    const identitiesSetup = mainStore.settingsStore.identitiesSetup;
    const muiTheme = useTheme();
    const accentColor = muiTheme.palette.primary.main;

    return (
        <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                        Identity Server
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Configure your identity server connection
                    </Typography>
                </Box>
            </Box>

            {/* Status alerts */}
            <Collapse in={!!identitiesSetup.connectionError}>
                <Alert
                    severity="error"
                    icon={<LinkOff fontSize="small" />}
                    sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.85rem' } }}
                >
                    <strong>Connection Error</strong> — {identitiesSetup.connectionError}
                </Alert>
            </Collapse>

            <Collapse in={identitiesSetup.isConnected}>
                <Alert
                    severity="success"
                    icon={<CloudDone fontSize="small" />}
                    sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.85rem' } }}
                >
                    <strong>Connected</strong> — Successfully connected to the server
                </Alert>
            </Collapse>

            {/* Form fields */}
            <Stack spacing={2}>
                <TextField
                    label="Server Address"
                    placeholder="https://your-server.com"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Dns fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={identitiesSetup.serverAddress}
                    onChange={(event) => identitiesSetup.setServerAddress(event.target.value)}
                    disabled={identitiesSetup.isConnecting}
                    size="small"
                    fullWidth
                    sx={inputSx(accentColor)}
                />

                <TextField
                    label="Username"
                    placeholder="Enter your username"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AccountCircle fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={identitiesSetup.username}
                    onChange={(event) => identitiesSetup.setUsername(event.target.value)}
                    disabled={identitiesSetup.isConnecting}
                    size="small"
                    fullWidth
                    sx={inputSx(accentColor)}
                />

                <TextField
                    label="Password"
                    placeholder="Enter your password"
                    type="password"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock fontSize="small" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={identitiesSetup.password}
                    onChange={(event) => identitiesSetup.setPassword(event.target.value)}
                    disabled={identitiesSetup.isConnecting}
                    size="small"
                    fullWidth
                    sx={inputSx(accentColor)}
                />
            </Stack>

            {/* Actions */}
            <Stack direction="row" spacing={1.5}>
                <Button
                    variant="contained"
                    onClick={identitiesSetup.connect}
                    disabled={identitiesSetup.isConnected || identitiesSetup.isConnecting}
                    startIcon={
                        identitiesSetup.isConnecting
                            ? <CircularProgress size={16} color="inherit" />
                            : identitiesSetup.isConnected
                                ? <Check sx={{ fontSize: 18 }} />
                                : undefined
                    }
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        boxShadow: `0 2px 12px ${Color(accentColor).alpha(0.3).hexa()}`,
                        '&:hover': { boxShadow: `0 4px 20px ${Color(accentColor).alpha(0.4).hexa()}` },
                        '&.Mui-disabled': { boxShadow: 'none' },
                    }}
                >
                    {identitiesSetup.isConnected ? 'Connected' : identitiesSetup.isConnecting ? 'Connecting...' : 'Connect'}
                </Button>
                <Button
                    variant="outlined"
                    onClick={identitiesSetup.reset}
                    disabled={identitiesSetup.isConnecting}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        borderColor: Color('#fff').alpha(0.15).hexa(),
                        '&:hover': { borderColor: Color('#fff').alpha(0.3).hexa(), backgroundColor: Color('#fff').alpha(0.04).hexa() },
                    }}
                >
                    Reset
                </Button>
            </Stack>
        </Stack>
    );
});

export default IdentityServerConfig;
